import { Box, Stack, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { CreditCardType } from '@/logic/services';
import { DateTime } from '@/utility/luxon';
import { FC } from 'react';

import { TransactionRowInfo } from './transaction-row-info';
import { TransactionType } from '../../services/types';
import { pxToRem } from '@/theme/typography';
import { useRouter } from 'next/router';
import { checkIfDateIsOlderThanNowDate } from '@/logic/helpers/check-if-date-is-older-than-now-date';
import ImageBoxFuture from '@/components/shared/image-box-future';

const NewTransactionIcon = '/icons/profile/transactions/new-transaction.svg';
const RefundIcon = '/icons/profile/transactions/refund-icon.svg';
const TopUpIcon = '/icons/profile/transactions/top-up-icon.svg';
const CashbackIcon = '/icons/profile/transactions/cashback-icon.svg';

interface TransactionRowProps {
  type: TransactionType;
  amount: number;
  paymentMethod?: CreditCardType;
  expireDate?: string;
  isMada: boolean;
}

export const TransactionRow: FC<TransactionRowProps> = ({
  type,
  amount,
  paymentMethod,
  expireDate,
  isMada,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();

  const transactionColor = () => {
    if (expireDate && checkIfDateIsOlderThanNowDate(expireDate)) {
      return theme.palette.grey[40];
    } else {
      return amount >= 0 ? theme.palette.toast.success : theme.palette.error.main;
    }
  };

  const getTransactionTitle = (transactionType: TransactionType) => {
    return intl.formatMessage({
      id: `wallet.transaction.type.${transactionType.toLowerCase()}`,
    });
  };

  const getTransactionImage = (transactionType: TransactionType) => {
    if (transactionType === 'CASHBACK') {
      return CashbackIcon;
    }
    if (transactionType === 'REDEEM') {
      return NewTransactionIcon;
    }
    if (transactionType === 'REFUND') {
      return RefundIcon;
    }
    if (transactionType === 'TOP_UP') {
      return TopUpIcon;
    }
  };

  const getTransactionAmount = (amount: number) => {
    return amount >= 0 ? `+${amount.toFixed(2)}` : amount.toFixed(2);
  };

  const getTransactionPaymentMethod = (
    paymentMethod: CreditCardType,
    _isMada = false
  ) => {
    return !!_isMada
      ? 'Mada'
      : intl.formatMessage({
          id: `my_credit_cards.supported_cards.${paymentMethod.toLowerCase()}`,
        });
  };

  const getTransactionExpiryDate = (expiryDate: string) => {
    return DateTime.fromISO(expiryDate, {
      locale,
    }).toFormat('DD');
  };

  return (
    <Stack
      direction='row'
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '16px',
        opacity: expireDate && checkIfDateIsOlderThanNowDate(expireDate) ? 0.4 : 1,
      }}
    >
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
        }}
      >
        <Stack
          direction='row'
          sx={{
            width: '40px',
            height: '40px',
            backgroundColor: theme.palette.grey[40],
            borderRadius: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px',
          }}
        >
          <ImageBoxFuture
            img={getTransactionImage(type)}
            title={intl.formatMessage({
              id: 'alt.transaction.icon',
            })}
            objectFit='contain'
            sx={{
              width: {
                mobile: '40px',
                tablet: pxToRem(33),
              },
              height: {
                mobile: '24px',
                tablet: pxToRem(33),
              },
            }}
          />
        </Stack>

        <Box
          sx={{
            marginLeft: '20px',
          }}
        >
          <Typography
            component='span'
            sx={{
              fontSize: pxToRem(18),
              fontWeight: '700',
            }}
          >
            {getTransactionTitle(type)}
          </Typography>

          {paymentMethod && (
            <TransactionRowInfo
              title={intl.formatMessage({ id: 'wallet.payment.method' })}
              description={getTransactionPaymentMethod(paymentMethod, isMada)}
            />
          )}

          {expireDate && (
            <TransactionRowInfo
              title={intl.formatMessage({
                id:
                  expireDate && checkIfDateIsOlderThanNowDate(expireDate)
                    ? 'wallet.expired.at'
                    : 'wallet.expires.on',
              })}
              description={getTransactionExpiryDate(expireDate)}
            />
          )}
        </Box>
      </Stack>

      <Stack
        direction='row'
        sx={{
          alignItems: 'baseline',
        }}
      >
        <Typography
          sx={{
            fontSize: pxToRem(18),
            fontWeight: '700',
            color: transactionColor,
          }}
        >
          {getTransactionAmount(amount)}
        </Typography>
        &nbsp;
        <Typography
          sx={{
            fontSize: pxToRem(16),
            fontWeight: '400',
            color: transactionColor,
          }}
        >
          <FormattedMessage id='currency.SAR' />
        </Typography>
      </Stack>
    </Stack>
  );
};
